// src/components/weeklyAwards/WeeklyLineupCard.js
// "Haftanın Karması" — gerçek bir saha çizimi üzerinde haftanın kadrosunu
// gösteren kart. Sağ sütunda, puan tablosunun hemen üstünde ve puan tablosuyla
// aynı genişlikte yer alır. Kaç oyuncu seçildiyse başlık ve dizilim ona göre
// güncellenir ("Haftanın 8'i" gibi). Her oyuncu tıklanabilir (profiline gider)
// ve altında kulüp takımı yazar.
import React from "react";
import { Link } from "react-router-dom";
import { formatEtl } from "../../api/marketValueApi";
import "./weeklyLineupCard.css";

// 1–11 arası sayılar için Türkçe iyelik eki ("Haftanın 8'i", "7'si" ...).
const COUNT_SUFFIX = {
  1: "'i", 2: "'si", 3: "'ü", 4: "'ü", 5: "'i", 6: "'sı",
  7: "'si", 8: "'i", 9: "'u", 10: "'u", 11: "'i",
};
const countTitle = (n) => `Haftanın ${n}${COUNT_SUFFIX[n] || "'si"}`;

function rowOf(slot) {
  const s = String(slot || "").toUpperCase();
  if (s.startsWith("GK")) return "gk";
  if (s.startsWith("DEF")) return "def";
  if (s.startsWith("MID")) return "mid";
  if (s.startsWith("FWD")) return "fwd";
  return "mid";
}

// Futbol sahası çizgileri. Dikey çizim varsayılan; yatay çizim, kartın geniş
// ekranda yatay sahaya geçtiği yerlerde (CSS ile) gösterilir.
function PitchLines() {
  return (
    <>
      <svg className="wl-lines wl-lines--v" viewBox="0 0 100 150" preserveAspectRatio="none" aria-hidden="true">
        <rect x="2" y="2" width="96" height="146" rx="3" />
        <line x1="2" y1="75" x2="98" y2="75" />
        <circle cx="50" cy="75" r="11" />
        <circle cx="50" cy="75" r="1.2" className="wl-dot" />
        {/* üst kale */}
        <rect x="26" y="2" width="48" height="18" />
        <rect x="38" y="2" width="24" height="8" />
        <path d="M36 20 A 14 10 0 0 0 64 20" />
        {/* alt kale */}
        <rect x="26" y="130" width="48" height="18" />
        <rect x="38" y="140" width="24" height="8" />
        <path d="M36 130 A 14 10 0 0 1 64 130" />
      </svg>
      <svg className="wl-lines wl-lines--h" viewBox="0 0 150 100" preserveAspectRatio="none" aria-hidden="true">
        <rect x="2" y="2" width="146" height="96" rx="3" />
        <line x1="75" y1="2" x2="75" y2="98" />
        <circle cx="75" cy="50" r="11" />
        <circle cx="75" cy="50" r="1.2" className="wl-dot" />
        {/* sol kale */}
        <rect x="2" y="26" width="18" height="48" />
        <rect x="2" y="38" width="8" height="24" />
        <path d="M20 36 A 10 14 0 0 1 20 64" />
        {/* sağ kale */}
        <rect x="130" y="26" width="18" height="48" />
        <rect x="140" y="38" width="8" height="24" />
        <path d="M130 36 A 10 14 0 0 0 130 64" />
      </svg>
    </>
  );
}

function PlayerPin({ p, playerLink }) {
  const name = p.player_name || `#${p.player_id}`;
  const initial = name.trim().charAt(0).toUpperCase();
  const inner = (
    <>
      <span className="wl-pin__avatar">
        {p.player_img ? <img src={p.player_img} alt="" loading="lazy" /> : <span className="wl-pin__ph">{initial}</span>}
      </span>
      <span className="wl-pin__name">{name}</span>
      {p.player_team_name && <span className="wl-pin__team">{p.player_team_name}</span>}
      {p.market_value != null && <span className="wl-pin__val">{formatEtl(p.market_value)}</span>}
    </>
  );
  if (p.player_id && playerLink) {
    return (
      <Link className="wl-pin" to={playerLink(p.player_id)} title={name}>
        {inner}
      </Link>
    );
  }
  return <div className="wl-pin" title={name}>{inner}</div>;
}

export default function WeeklyLineupCard({ set, playerLink }) {
  if (!set) return null;
  const squad = (set.items || []).filter((i) => i.award_type === "WEEKLY_SQUAD_PLAYER");
  if (squad.length === 0) return null;

  const rows = { fwd: [], mid: [], def: [], gk: [] };
  squad
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .forEach((p) => rows[rowOf(p.position_slot)].push(p));

  const orderedRows = ["fwd", "mid", "def", "gk"].filter((r) => rows[r].length > 0);

  return (
    <article className="lm-panel wl">
      <div className="lm-panel__head wl__head">
        <div>
          <span className="lm-panel__eyebrow">Haftanın Karması</span>
          <h2 className="lm-panel__title">{countTitle(squad.length)}</h2>
        </div>
        {set.formation && <span className="wl__formation">{set.formation}</span>}
      </div>

      <div className="wl__pitch">
        <PitchLines />
        <div className="wl__rows">
          {orderedRows.map((r) => (
            <div className={`wl__row wl__row--${r}`} key={r}>
              {rows[r].map((p) => (
                <PlayerPin key={p.id || `${p.player_id}-${p.position_slot}`} p={p} playerLink={playerLink} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </article>
  );
}
